import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  SafeAreaView,
  View,
  useWindowDimensions,
  Clipboard,
  Platform,
} from 'react-native';
import {
  Button,
  Divider,
  IconButton,
  List,
  Menu,
  TextInput,
} from 'react-native-paper';
import {
  EditorLayer,
  EditorProject,
  FFMpegOutputType,
  FFMpegPreset,
} from '../../types';
import {
  genRenderCmd,
  genExecTime,
  getTotalTime,
  encodeProject,
} from '../../api/media';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  addNewLogLine,
  setActiveLayer,
  setCaretPosition,
  setIsRendering,
  setRenderProgress,
  setRenderUrl,
  setScrollPosition,
} from '../../store/slices/editor';
import {useMutation} from 'react-query';
import SyntaxHighlighter from 'react-native-syntax-highlighter';
import {xonokai} from 'react-syntax-highlighter/src/styles/prism';
import CodeEditor, {
  CodeEditorSyntaxStyles,
} from '@rivascva/react-native-code-editor';
import {writeItem} from '../../api';
function EditorForm(props: {
  mediaRes: EditorProject;
  layers: EditorLayer[];
  video2: RefObject<JSX.IntrinsicElements['video'] | undefined>;
}) {
  const {mediaRes, video2, layers, projectQuery} = props;

  const dispatch = useAppDispatch();
  const caretPosition = useAppSelector(
    state => state.editor.caretPosition,
  );
  const scrollPosition = useAppSelector(
    state => state.editor.scrollPosition,
  );
  const totalTime = layers?.length ? getTotalTime(layers) : 0;
  const [showCode, setShowCode] = useState(false);
  const {width} = useWindowDimensions();
  const codeEditorRef = useRef();
  const fontSize = 12;
  const inputWidth = 120;
  const codeWidth = width * 0.24 - 50;
  const [outputMenuVisible, setOutputMenuVisible] = useState(false);
  const [presetMenuVisible, setPresetMenuVisible] = useState(false);
  const outputTypes = Object.keys(FFMpegOutputType);
  const execCmd = genRenderCmd(mediaRes, layers, true).execCmd;
  const codeString = execCmd.join('\n');
  const closeMenu = () => {
    setOutputMenuVisible(false);
    setPresetMenuVisible(false);
  };
  const ffmpegLogs = useAppSelector(state => state.editor.ffmpegLogs);
  const projectWriteMutation = useMutation(
    (item: EditorProject) => writeItem(item),
    {
      onSuccess: () => {
        console.info('after writing project');
        projectQuery.refetch();
      },
    },
  );
  const processKey = useCallback(
    ev => {
      const step = 5;
      if (ev.code === 'ArrowLeft') {
        if (!video2.current?.paused) {
          video2.current?.pause();
        }
        console.info(
          'arrowLeft, caretPosition, scrollPosition',
          caretPosition,
          scrollPosition,
        );
        caretPosition > 0
          ? dispatch(setCaretPosition(caretPosition - step))
          : dispatch(setScrollPosition(scrollPosition - step));
      }
      if (ev.code === 'ArrowRight') {
        if (!video2.current?.paused) {
          video2.current?.pause();
        }
        console.info(
          'arrowRight, caretPosition, scrollPosition',
          caretPosition,
          scrollPosition,
        );
        dispatch(setCaretPosition(caretPosition + step));
      }
      if (ev.code === 'Space') {
        if (video2.current?.paused) video2.current?.play();
        else video2.current?.pause();
      }
    },
    [caretPosition, scrollPosition],
  );
  useEffect(() => {
    if (Platform.OS === 'web') {
      document.onkeyup = ev => processKey(ev);
    }
  }, [processKey]);
  const selectValue = (
    valueType: keyof EditorProject['editor'],
    value: string | number,
  ) => {
    const np = mediaRes;
    const numericTypes = [
      'width',
      'height',
      'start',
      'end',
      'framerate',
    ];
    const nonRenderTypes = ['start', 'end'];
    const shouldRerender = !nonRenderTypes.includes(valueType);
    if (numericTypes.includes(valueType) && isNaN(value)) {
      console.info('invalid type');
      return;
    }
    if (!np.editor) {
      np.editor = {};
    }
    np.editor[valueType] = value;

    if (shouldRerender) np.editor.lastUpdated = new Date().valueOf();

    projectWriteMutation.mutateAsync(np);
    closeMenu();
  };

  console.info('render config');
  return (
    <>
      <List.Item
        key={'start'}
        left={p => (
          <List.Icon {...p} style={{fontSize}} icon={'clock-plus'} />
        )}
        titleStyle={{fontSize}}
        title={'Start'}
        right={p => (
          <View style={{width: inputWidth}}>
            <TextInput
              // dense={true}
              // mode="outlined"
              value={'00:00:00'}
              style={{textAlign: 'right', pointerEvents: 'auto'}}
              keyboardType="numeric"
              onChangeText={text =>
                selectValue('start', parseInt(text, 10))
              }
            />
          </View>
        )}
      />
      <Divider key={'div7'} />
      <List.Item
        key={'end'}
        left={p => (
          <List.Icon {...p} style={{fontSize}} icon={'clock-minus'} />
        )}
        titleStyle={{fontSize}}
        title={`End`}
        right={p => (
          <View style={{width: inputWidth}}>
            <TextInput
              // dense={true}
              // mode="outlined"
              value={genExecTime(totalTime) || 0}
              style={{textAlign: 'right', pointerEvents: 'auto'}}
              keyboardType="numeric"
              focusable={true}
              onChangeText={text =>
                selectValue('end', parseInt(text, 10))
              }
            />
          </View>
        )}
      />
      <Divider key={'div3'} />
      <List.Item
        key={'width'}
        left={p => (
          <List.Icon {...p} style={{fontSize}} icon={'ruler'} />
        )}
        title={`Width`}
        titleStyle={{fontSize}}
        right={p => (
          <View style={{width: inputWidth}}>
            <TextInput
              // dense={true}
              // mode="outlined"
              value={mediaRes?.editor?.width.toString()}
              style={{textAlign: 'right', pointerEvents: 'auto'}}
              focusable={true}
              keyboardType="numeric"
              onChangeText={text =>
                selectValue('width', parseInt(text, 10))
              }
            />
          </View>
        )}
      />
      <Divider key={'div2'} />
      <List.Item
        key={'height'}
        left={p => (
          <List.Icon {...p} style={{fontSize}} icon={'ruler'} />
        )}
        title={`Height`}
        titleStyle={{fontSize}}
        right={p => (
          <View style={{width: inputWidth}}>
            <TextInput
              // dense={true}
              // mode="outlined"
              value={mediaRes?.editor?.height.toString()}
              style={{textAlign: 'right', pointerEvents: 'auto'}}
              focusable={true}
              keyboardType="numeric"
              onChangeText={text =>
                selectValue('height', parseInt(text, 10))
              }
            />
          </View>
        )}
      />
      <Divider key={'divaspect'} />
      <List.Item
        key={'aspect'}
        left={p => (
          <List.Icon
            {...p}
            style={{fontSize}}
            icon={'aspect-ratio'}
          />
        )}
        titleStyle={{fontSize}}
        title={'Aspect Ratio'}
        // disabled={true}
        onPress={() => {}}
        right={p => (
          <View style={{width: inputWidth}}>
            <TextInput
              // dense={true}
              // mode="outlined"
              value={mediaRes?.editor?.aspectRatio}
              style={{textAlign: 'right'}}
              onChangeText={text => {
                console.info('onchangetext', text);
                if (
                  text !== '' &&
                  text !== mediaRes?.editor?.aspectRatio
                ) {
                  selectValue('aspectRatio', text);
                }
              }}
            />
          </View>
        )}
      />
      <Divider key={'divrate'} />
      <List.Item
        key={'frames'}
        left={p => (
          <List.Icon {...p} style={{fontSize}} icon={'movie-roll'} />
        )}
        titleStyle={{fontSize}}
        title={'Frame Rate'}
        right={p => (
          <View style={{width: inputWidth}}>
            <TextInput
              // dense={true}
              // mode="outlined"
              value={mediaRes?.editor?.framerate?.toString() || '30'}
              style={{
                textAlign: 'right',
                pointerEvents: 'auto',
                fontSize: 14,
              }}
              keyboardType="numeric"
              onChangeText={text =>
                selectValue('framerate', parseInt(text, 10))
              }
            />
          </View>
        )}
      />
      <Divider key={'div1'} />
      <List.Item
        key={'output'}
        left={p => (
          <List.Icon {...p} style={{fontSize}} icon={'file-export'} />
        )}
        title={'Output'}
        titleStyle={{fontSize}}
        right={p => (
          <View style={{width: inputWidth}}>
            <Menu
              visible={outputMenuVisible}
              onDismiss={closeMenu}
              anchor={
                <Button
                  mode="outlined"
                  onPress={() => setOutputMenuVisible(true)}>
                  {mediaRes?.editor?.output}
                </Button>
              }>
              <>
                {outputTypes.map(item => (
                  <Menu.Item
                    key={item}
                    onPress={() => selectValue('output', item)}
                    title={item}
                  />
                ))}
              </>
            </Menu>
          </View>
        )}
      />
      <Divider key={'divPreset'} />
      <List.Item
        key={'preset'}
        left={p => (
          <List.Icon {...p} style={{fontSize}} icon={'file-export'} />
        )}
        title={'Preset'}
        titleStyle={{fontSize}}
        right={p => (
          <View style={{width: inputWidth}}>
            <Menu
              visible={presetMenuVisible}
              onDismiss={closeMenu}
              anchor={
                <Button
                  mode="outlined"
                  onPress={() => setPresetMenuVisible(true)}>
                  {mediaRes?.editor?.preset}
                </Button>
              }>
              <>
                {Object.keys(FFMpegPreset).map(item => (
                  <Menu.Item
                    key={item}
                    onPress={() => selectValue('preset', item)}
                    title={item}
                  />
                ))}
              </>
            </Menu>
          </View>
        )}
      />
      <Divider key={'divinputcode2'} />
      <Button
        icon={'code-brackets'}
        onPress={() => setShowCode(!showCode)}>
        Show FFMpeg Code
      </Button>
      {showCode === true ? (
        <View>
          <Divider key={'divinputcode'} />
          <List.Item
            key={'code'}
            left={p => <List.Icon {...p} icon={'code-brackets'} />}
            title={''}
            titleStyle={{fontSize}}
            onPress={() => codeEditorRef?.current?.focus()}
            right={p => (
              <SafeAreaView
                style={{
                  width: Platform.OS === 'web' ? codeWidth : '100%',
                  zIndex: 10,
                }}>
                <CodeEditor
                  className={'codeEditor'}
                  ref={codeEditorRef}
                  style={{
                    fontSize,
                    inputLineHeight: fontSize,
                    highlighterLineHeight: fontSize,
                    marginLeft: 0,
                  }}
                  language="bash"
                  syntaxStyle={CodeEditorSyntaxStyles.xonokai}
                  initialValue={
                    mediaRes.editor?.code?.join(',') || ''
                  }
                  onChange={text => {
                    if (
                      text === mediaRes.editor?.code?.join(',') ||
                      ''
                    )
                      return;
                    selectValue('code', text.split(','));
                  }}
                  autoFocus={false}
                  showLineNumbers
                />
              </SafeAreaView>
            )}
          />
          {Platform.OS === 'web' ? (
            <>
              <Divider key={'divoutputcode'} />
              {codeString && (
                <List.Item
                  key={'output_code'}
                  left={p => <List.Icon {...p} icon={'code-tags'} />}
                  title=""
                  onPress={() => Clipboard.setString(codeString)}
                  titleStyle={{fontSize}}
                  right={() => (
                    <View style={{width: codeWidth}}>
                      <SyntaxHighlighter
                        language="bash"
                        style={xonokai}
                        fontSize={fontSize}
                        pointerEvents={true}
                        scroll={true}
                        highlighter={'prism'}>
                        {codeString}
                      </SyntaxHighlighter>{' '}
                    </View>
                  )}
                />
              )}
              <Divider key={'divlogs'} />
              {codeString && (
                <List.Item
                  key={'output_logs'}
                  left={p => (
                    <List.Icon {...p} icon={'code-parentheses'} />
                  )}
                  title=""
                  onPress={() => Clipboard.setString(ffmpegLogs)}
                  titleStyle={{fontSize}}
                  right={() => (
                    <View
                      style={{
                        width: codeWidth,
                        pointerEvents: 'auto',
                      }}>
                      <SyntaxHighlighter
                        language="bash"
                        style={xonokai}
                        customStyle={{
                          pointerEvents: 'auto',
                          userSelect: 'all',
                        }}
                        fontSize={fontSize}
                        userSelect={true}
                        pointerEvents={true}
                        scroll={true}
                        highlighter={'prism'}>
                        {ffmpegLogs}
                      </SyntaxHighlighter>{' '}
                    </View>
                  )}
                />
              )}
              <Divider key={'div8'} />
            </>
          ) : null}
        </View>
      ) : null}
    </>
  );
}

export default EditorForm;
